import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Col, Row, Spinner } from "reactstrap";
import GoBack from "../../components/GoBack";
import { AI_REFERENCE_QUESTIONS, CANDIDATE_SEARCH_DEV, CHATGPT_TEMPLATES, CSV_IMPORT, EMAIL_TEMPLATES, MODULE_ACCESS, RECRUITER_DEAL_TEMPLATES, RESUME_PARSING, STARTER_INTERVIEW_QUESTIONS, SYNONYM_LIST, TEMP_BILLING_IMPORT, TEMP_BILLING_RECONCILIATION, USER_ACTIVITY_REPORT } from "../../constants";
import { AppContext } from "../../context";
import { ACCOUNTING_DASHBOARD_ROUTE, ADMIN_IMPORT_CSV, AI_RECRUITER_HEALTH, AI_REFERENCE_QUESTIONS_ROUTE, AI_RECRUITER_HEALTH_ROUTE, BILLING_CREDITS_ADMIN_ROUTE, CANDIDATE_SEARCH_DEV_ROUTE, CHATGPT_TEMPLATE_ROUTE, STARTER_INTERVIEW_QUESTIONS_ROUTE, EMAIL_TEMPLATES_ROUTE, MODULE_ROUTE, RECRUITER_DEAL_TEMPLATES_ROUTE, RESUME_PARSER_COMPARE_ROUTE, ROLES, SETTINGS_ROUTE, SYNONYM_ROUTE, TEMP_BILLING_COST_RATES, TEMP_BILLING_COST_RATES_ROUTE, TEMP_BILLING_IMPORTS_ROUTE, TEMP_BILLING_RECONCILIATION_ROUTE, USER_ACTIVITY_REPORT_ROUTE } from "../../utils/constant";
import ResumeParsing from "../resumeParsing";
// kan-66: same query PrivateRoute issues — read here so the role check is
// correct on the FIRST paint, before the AppContext roles are filled.
import { FETCH_CURRENT_USER } from "../settings/gql";
import {
  GET_ADMIN_STATS,
  PLACEMENT_ADMIN_STATS,
  SYNC_INTERSELLER_USERS_TO_ATS,
  SYNC_INTERSELLER_WEBHOOKS,
} from "./gql";

const StatCard = ({
  loading,
  title,
  rightStatText,
  rightStatCount = 0,
  leftStatText,
  leftStatCount = 0,
  manageLink = "",
  buttonText,
  onClick
}: {
  loading?: boolean;
  title: string;
  rightStatText?: string;
  rightStatCount?: number | string;
  leftStatText?: string;
  leftStatCount?: number | string;
  manageLink?: string;
  buttonText?: string
  onClick?: () => void
}) => (
  <div className="users-card">
    <div className="d-flex">
      <h5>{title}</h5>
      {buttonText ? <h5
        className="anchorColorText route-link" onClick={onClick}>{buttonText}</h5> :
        <Link to={manageLink}>Manage</Link>
      }
    </div>
    <div className="d-flex m-0">
      <h5 className="d-flex m-0 flex-wrap">
        {leftStatText && (
          <>
            <span className="text-success mr-1"> {leftStatText}:</span>
            <strong className="font-weight-bold">
              {loading ? <Spinner size="sm" className="mb-1" /> : leftStatCount}
            </strong>
          </>
        )}
      </h5>
      <h5 className="d-flex m-0 flex-wrap">
        {rightStatText && (
          <>
            <span className="text-warning mr-1"> {rightStatText}: </span>
            <strong className="font-weight-bold">
              {loading ? (
                <Spinner size="sm" className="mb-1" />
              ) : (
                rightStatCount
              )}
            </strong>
          </>
        )}
      </h5>
    </div>
  </div>
);

const IntersellerUserSyncCard = ({
  loading,
  title,
  rightStatText,
  rightStatCount = 0,
  leftStatText,
  leftStatCount = 0,
  manageLink = "",
  adminStatsRefetch,
}: {
  loading?: boolean;
  title: string;
  rightStatText?: string;
  rightStatCount?: number | string;
  leftStatText?: string;
  leftStatCount?: number | string;
  manageLink?: string;
  adminStatsRefetch: Function;
}) => {
  const [syncIntersellerUsers, { loading: syncIntersellerContactsLoading }] =
    useMutation(SYNC_INTERSELLER_USERS_TO_ATS);

  const syncIntersellerUsersToATS = async () => {
    const response = await syncIntersellerUsers({ variables: {} });
    if (response?.data?.syncIntersellerUsersToATS?.status === 200) {
      adminStatsRefetch();
      toast.success("Successfully synced interseller users to ATS");
    } else if (response?.data?.syncIntersellerUsersToATS?.status === 304) {
      toast.warning("Nothing available to sync interseller users to ATS");
    }
  };

  useEffect(() => {
    document.title = "Admin - RealREPP";

    // eslint-disable-next-line
  }, []);

  return (
    <div className="users-card">
      <div className="d-flex">
        <h5>{title}</h5>

        <Link to={manageLink} onClick={syncIntersellerUsersToATS}>
          Sync Users
          {syncIntersellerContactsLoading ? (
            <span>
              <Spinner size="sm" className="mb-1" />
            </span>
          ) : null}
        </Link>
      </div>
      <div className="d-flex m-0">
        <h5 className="d-flex m-0">
          {leftStatText && (
            <>
              <span className="text-success mr-1"> {leftStatText}:</span>
              <strong className="font-weight-bold">
                {loading ? (
                  <Spinner size="sm" className="mb-1" />
                ) : (
                  leftStatCount
                )}
              </strong>
            </>
          )}
        </h5>
        <h5 className="d-flex m-0">
          {rightStatText && (
            <>
              <span className="text-warning mr-1"> {rightStatText}: </span>
              <strong className="font-weight-bold">
                {loading ? (
                  <Spinner size="sm" className="mb-1" />
                ) : (
                  rightStatCount
                )}
              </strong>
            </>
          )}
        </h5>
      </div>
    </div>
  );
};

const IntersellerWebhookCard = ({
  loading,
  title,
  rightStatText,
  rightStatCount = 0,
  leftStatText,
  leftStatCount = 0,
  manageLink = "",
  adminStatsRefetch,
}: {
  loading?: boolean;
  title: string;
  rightStatText?: string;
  rightStatCount?: number | string;
  leftStatText?: string;
  leftStatCount?: number | string;
  manageLink?: string;
  adminStatsRefetch: Function;
}) => {
  const [
    syncIntersellerWebhooks,
    {
      // data: syncIntersellerContactsData,
      loading: syncIntersellerWebhooksLoading,
    },
  ] = useMutation(SYNC_INTERSELLER_WEBHOOKS);

  const syncIntersellerWebhooksHandler = async () => {
    const res = await syncIntersellerWebhooks({ variables: {} });
    if (res?.data) {
      adminStatsRefetch();
      toast.success("Successfully Synced webhooks against users");
    }
  };

  useEffect(() => {
    document.title = "Admin - RealREPP";

    // eslint-disable-next-line
  }, []);

  return (
    <div className="users-card">
      <div className="d-flex">
        <h5>{title}</h5>

        <Link to={manageLink} onClick={syncIntersellerWebhooksHandler}>
          Sync Webhooks
          {syncIntersellerWebhooksLoading ? (
            <span>
              <Spinner size="sm" className="mb-1" />
            </span>
          ) : null}
        </Link>
      </div>
      <div className="d-flex m-0">
        <h5 className="d-flex m-0">
          {leftStatText && (
            <>
              <span className="text-success mr-1"> {leftStatText}:</span>
              <strong className="font-weight-bold">
                {loading ? (
                  <Spinner size="sm" className="mb-1" />
                ) : (
                  leftStatCount
                )}
              </strong>
            </>
          )}
        </h5>
        <h5 className="d-flex m-0">
          {rightStatText && (
            <>
              <span className="text-warning mr-1"> {rightStatText}: </span>
              <strong className="font-weight-bold">
                {loading ? (
                  <Spinner size="sm" className="mb-1" />
                ) : (
                  rightStatCount
                )}
              </strong>
            </>
          )}
        </h5>
      </div>
    </div>
  );
};

const Admin = (): JSX.Element => {
  const [modal, setModal] = useState<boolean>(false)
  const [adminStats, { data, loading }] = useLazyQuery(GET_ADMIN_STATS);
  const { userRoles } = useContext(AppContext);

  const [
    adminPlacementStats,
    { data: adminPlacementStatsData, loading: adminPlacementStatsLoading },
  ] = useLazyQuery(PLACEMENT_ADMIN_STATS);

  // kan-66 — D4 FIRST-PAINT correctness. `AppContext.userRoles` starts as []
  // and is only filled by PrivateRoute's effect once FETCH_CURRENT_USER
  // resolves, while PrivateRoute renders this component IMMEDIATELY (its
  // `userRoles?.length` branch falls through to <Component/> when the query is
  // still in flight). Deriving the role check from the context alone therefore
  // evaluates `isAiRecruiterOnlyAdmin` as FALSE on the first paint of a direct
  // load/refresh of /admin, flashing the full grid — including the seven cards
  // that carry no role condition at all — to an AI-Recruiter-only user before
  // collapsing to the single console card.
  //
  // Reading the same query here closes that gap: PrivateRoute has already
  // issued it, so this is a cache read (no extra round trip), and it gives us
  // the roles on the very first render. The context stays the primary source
  // once populated; the query is only the pre-context fallback. Role names are
  // mapped exactly as PrivateRoute maps them, so both agree row-for-row.
  const { data: currentUserData, loading: currentUserLoading } =
    useQuery(FETCH_CURRENT_USER);
  const resolvedRoles: string[] = userRoles?.length
    ? userRoles
    : (currentUserData?.me?.user?.userRoles ?? []).map(
        (item: any) => item?.role?.name,
      );

  // kan-66 — D4. The four roles below are the ONLY roles that granted /admin
  // before this ticket (see routes.tsx '/admin'). A user whose admin access comes
  // SOLELY from AI_RECRUITER holds none of them, so this is false for every
  // pre-existing admin user and their view is provably unchanged.
  const ADMIN_GRANTING_ROLES = [
    ROLES.ADMIN,
    ROLES.EXECUTIVE,
    ROLES.ACCOUNTING_MANAGER,
    ROLES.ACCOUNTING_COORDINATOR,
  ];
  const isAiRecruiterOnlyAdmin =
    !!resolvedRoles?.includes(ROLES.AI_RECRUITER) &&
    !resolvedRoles?.some((r: string) => ADMIN_GRANTING_ROLES.includes(r));

  // kan-66: keeps the stats queries exactly-once for existing admins even though
  // the effect below now re-runs when roles arrive.
  const statsRequestedRef = useRef(false);

  useEffect(() => {
    // kan-66: roles arrive ASYNCHRONOUSLY — PrivateRoute fills the context in
    // its own effect, and React runs CHILD effects before PARENT effects, so on
    // first mount the context is still []. With a `[]` dep array the guard below
    // would therefore never see the AI_RECRUITER role and both queries would
    // fire anyway. Waiting for roles is what actually suppresses them.
    if (currentUserLoading) return;
    if (!resolvedRoles?.length) return;
    // Both queries are Admin-gated server-side; an AI-Recruiter-only admin
    // would just get a 403 back, so don't fire them at all.
    if (isAiRecruiterOnlyAdmin) return;
    if (statsRequestedRef.current) return;
    statsRequestedRef.current = true;
    adminPlacementStats({ variables: {} });
    adminStats({ variables: {} });

    // eslint-disable-next-line
  }, [userRoles, currentUserLoading, isAiRecruiterOnlyAdmin]);

  const toggle = () => setModal(!modal)

  // kan-66 — D4. Render NOTHING but a spinner until the caller's roles are
  // known. Both role sources are empty while FETCH_CURRENT_USER is in flight
  // (the context has not been filled and the query has no data yet), and the
  // grid below contains seven cards with no role condition at all — so
  // rendering it now would flash the full admin surface to an
  // AI-Recruiter-only user on every direct load/refresh of /admin.
  // This only defers the FIRST paint: `currentUserLoading` always resolves, and
  // a user who genuinely has no roles still falls through to the grid exactly
  // as before, so no existing role's view changes.
  if (currentUserLoading && !userRoles?.length) {
    return (
      <div className="admin-tabs">
        <GoBack url={SETTINGS_ROUTE} />

        <div className="priamryHeading primaryHeaderSpacing">
          <h5 className="m-0">Admin</h5>
        </div>

        <div className="d-flex justify-content-center py-5">
          <Spinner />
        </div>
      </div>
    );
  }

  if (isAiRecruiterOnlyAdmin) {
    return (
      <div className="admin-tabs">
        <GoBack url={SETTINGS_ROUTE} />

        <div className="priamryHeading primaryHeaderSpacing">
          <h5 className="m-0">Admin</h5>
        </div>

        <Row>
          <Col xl="4" lg="6" md="6" xs="12">
            <StatCard
              title="AI Recruiter Console"
              manageLink="/ai-recruiter-console"
            />
          </Col>
        </Row>
      </div>
    );
  }

  // Cards are declared with their original per-card role conditions and
  // rendered ALPHABETICALLY by title. Only the ordering is new — every
  // `visible` predicate below is verbatim what used to gate that card's JSX.
  const hasAdmin = !!userRoles?.length && userRoles?.includes(ROLES.ADMIN);
  const adminCards: { title: string; visible: boolean; card: JSX.Element }[] = [
    {
      title: 'Users',
      visible: hasAdmin,
      card: (
        <StatCard
          title="Users"
          manageLink="/admin/users"
          loading={loading}
          leftStatText="Total"
          leftStatCount={data?.adminStats?.users || 0}
        />
      ),
    },
    {
      title: 'Teams',
      visible: hasAdmin,
      card: (
        <StatCard
          title="Teams"
          manageLink="/admin/teams"
          leftStatText="Active"
          loading={loading}
          leftStatCount={data?.adminStats?.activeTeams || 0}
          rightStatText="Pending"
          rightStatCount={data?.adminStats?.pendingTeams || 0}
        />
      ),
    },
    {
      title: 'Merge Companies',
      visible: hasAdmin,
      card: (
        <StatCard
          title="Merge Companies"
          manageLink="/merge-company"
          leftStatText="Merged"
          loading={loading}
          leftStatCount={data?.adminStats?.mergeCompanies || 0}
          rightStatText="Pending"
          rightStatCount={data?.adminStats?.pendingCompanies || 0}
        />
      ),
    },
    {
      title: 'Merge Contacts',
      visible: hasAdmin,
      card: (
        <StatCard
          title="Merge Contacts"
          manageLink="/merge-contacts"
          leftStatText="Merged"
          loading={loading}
          leftStatCount={data?.adminStats?.mergedContacts || 0}
          rightStatText="Pending"
          rightStatCount={data?.adminStats?.pendingContacts || 0}
        />
      ),
    },
    {
      title: 'Merge Candidates',
      visible: hasAdmin,
      card: (
        <StatCard
          title="Merge Candidates"
          manageLink="/merge-candidate"
          leftStatText="Merged"
          loading={loading}
          leftStatCount={data?.adminStats?.mergeCandidates || 0}
          rightStatText="Pending"
          rightStatCount={data?.adminStats?.pendingCandidates || 0}
        />
      ),
    },
    {
      title: 'Accounting Dashboard',
      visible:
        !!userRoles?.length &&
        (userRoles?.includes(ROLES.ACCOUNTING_MANAGER) ||
          userRoles?.includes(ROLES.EXECUTIVE)),
      card: (
        <StatCard
          title="Accounting Dashboard"
          manageLink={ACCOUNTING_DASHBOARD_ROUTE}
          leftStatText="Accounts Approved"
          loading={adminPlacementStatsLoading}
          leftStatCount={
            adminPlacementStatsData?.placementStatsForAdmin?.approved || 0
          }
          rightStatText="Pending Accounts"
          rightStatCount={
            adminPlacementStatsData?.placementStatsForAdmin?.pending || 0
          }
        />
      ),
    },
    {
      title: SYNONYM_LIST,
      visible:
        !!userRoles?.length &&
        (userRoles?.includes(ROLES.ADMIN) || userRoles?.includes(ROLES.EXECUTIVE)),
      card: <StatCard title={SYNONYM_LIST} manageLink={SYNONYM_ROUTE} />,
    },
    {
      // kan-66: ROLES.AI_RECRUITER added so a user who holds the new role
      // ALONGSIDE another admin-granting role (e.g. Accounting Manager) still
      // gets a link to the console — they fall through the isAiRecruiterOnlyAdmin
      // branch above and would otherwise reach /admin with no way in.
      title: 'AI Recruiter Console',
      visible:
        !!userRoles?.length &&
        (userRoles?.includes(ROLES.ADMIN) ||
          userRoles?.includes(ROLES.EXECUTIVE) ||
          userRoles?.includes(ROLES.AI_RECRUITER)),
      card: (
        <StatCard title="AI Recruiter Console" manageLink="/ai-recruiter-console" />
      ),
    },
    {
      // kan-87: diagnostic tool — runs Sovren and the Claude resume parser
      // against a stored resume and diffs the normalized output. Read-only.
      title: 'Resume Parser Compare',
      visible:
        !!userRoles?.length &&
        (userRoles?.includes(ROLES.ADMIN) || userRoles?.includes(ROLES.EXECUTIVE)),
      card: (
        <StatCard
          title="Resume Parser Compare"
          manageLink={RESUME_PARSER_COMPARE_ROUTE}
        />
      ),
    },
    {
      title: MODULE_ACCESS,
      visible: true,
      card: (
        <StatCard title={MODULE_ACCESS} manageLink={MODULE_ROUTE} loading={loading} />
      ),
    },
    {
      title: CHATGPT_TEMPLATES,
      visible: true,
      card: (
        <StatCard
          title={CHATGPT_TEMPLATES}
          manageLink={CHATGPT_TEMPLATE_ROUTE}
          loading={loading}
        />
      ),
    },
    {
      title: STARTER_INTERVIEW_QUESTIONS,
      visible: true,
      card: (
        <StatCard
          title={STARTER_INTERVIEW_QUESTIONS}
          manageLink={STARTER_INTERVIEW_QUESTIONS_ROUTE}
          loading={loading}
        />
      ),
    },
    {
      // kan-111: same visibility as the starter-questions card.
      title: AI_REFERENCE_QUESTIONS,
      visible: true,
      card: (
        <StatCard
          title={AI_REFERENCE_QUESTIONS}
          manageLink={AI_REFERENCE_QUESTIONS_ROUTE}
          loading={loading}
        />
      ),
    },
    {
      title: RESUME_PARSING,
      visible: true,
      card: (
        <StatCard
          title={RESUME_PARSING}
          buttonText="Manage"
          onClick={toggle}
          loading={loading}
        />
      ),
    },
    {
      title: CSV_IMPORT,
      visible: true,
      card: <StatCard title={CSV_IMPORT} manageLink={ADMIN_IMPORT_CSV} />,
    },
    {
      title: RECRUITER_DEAL_TEMPLATES,
      visible: true,
      card: (
        <StatCard
          title={RECRUITER_DEAL_TEMPLATES}
          manageLink={RECRUITER_DEAL_TEMPLATES_ROUTE}
        />
      ),
    },
    {
      title: AI_RECRUITER_HEALTH,
      visible: true,
      card: (
        <StatCard title={AI_RECRUITER_HEALTH} manageLink={AI_RECRUITER_HEALTH_ROUTE} />
      ),
    },
    {
      title: EMAIL_TEMPLATES,
      visible: !!userRoles?.includes("EXECUTIVE"),
      card: <StatCard title={EMAIL_TEMPLATES} manageLink={EMAIL_TEMPLATES_ROUTE} />,
    },
    {
      title: USER_ACTIVITY_REPORT,
      visible: !!userRoles?.includes("EXECUTIVE"),
      card: (
        <StatCard
          title={USER_ACTIVITY_REPORT}
          manageLink={USER_ACTIVITY_REPORT_ROUTE}
        />
      ),
    },
    {
      title: CANDIDATE_SEARCH_DEV,
      visible: !!userRoles?.includes("EXECUTIVE"),
      card: (
        <StatCard
          title={CANDIDATE_SEARCH_DEV}
          manageLink={CANDIDATE_SEARCH_DEV_ROUTE}
        />
      ),
    },
    {
      title: 'Billing Credits',
      visible: !!userRoles?.includes("EXECUTIVE"),
      card: (
        <StatCard title="Billing Credits" manageLink={BILLING_CREDITS_ADMIN_ROUTE} />
      ),
    },
    {
      title: TEMP_BILLING_IMPORT,
      visible:
        !!(
          userRoles?.includes(ROLES.ACCOUNTING_COORDINATOR) ||
          userRoles?.includes(ROLES.EXECUTIVE)
        ),
      card: (
        <StatCard title={TEMP_BILLING_IMPORT} manageLink={TEMP_BILLING_IMPORTS_ROUTE} />
      ),
    },
    {
      // kan-101: reconciling a People 2.0 payment recognises revenue, so it is
      // executive-only — the server enforces the same on every operation.
      title: TEMP_BILLING_RECONCILIATION,
      visible: !!userRoles?.includes(ROLES.EXECUTIVE),
      card: (
        <StatCard
          title={TEMP_BILLING_RECONCILIATION}
          manageLink={TEMP_BILLING_RECONCILIATION_ROUTE}
        />
      ),
    },
    {
      // The rate periods behind "Owed to RealREPP". Executive only: they decide
      // what the reconciliation says People 2.0 owes us.
      title: TEMP_BILLING_COST_RATES,
      visible: !!userRoles?.includes(ROLES.EXECUTIVE),
      card: (
        <StatCard
          title={TEMP_BILLING_COST_RATES}
          manageLink={TEMP_BILLING_COST_RATES_ROUTE}
        />
      ),
    },
  ];

  return (
    <div className="admin-tabs">
      <GoBack url={SETTINGS_ROUTE} />

      <div className="priamryHeading primaryHeaderSpacing">
        <h5 className="m-0">Admin</h5>
      </div>

      <Row>
        {adminCards
          .filter((item) => item.visible)
          .sort((a, b) => a.title.localeCompare(b.title))
          .map((item) => (
            <Col xl="4" lg="6" md="6" xs="12" key={item.title}>
              {item.card}
            </Col>
          ))}
      </Row>
      <ResumeParsing
        modal={modal}
        toggle={toggle} />
    </div>
  );
};

export default Admin;
